import React, { type ComponentProps, useMemo } from 'react';
import { ComboBox } from '@tackle-io/platform-ui';
import { InputLabel } from './InputLabel';
import { HelperText } from './HelperText';
import { Box } from 'vendor/material';

const DEFAULT_OPTION = '--None--';

interface SingleSelectProps {
  name: string;
  options: ComponentProps<typeof ComboBox>['options'];
  label: string;
  errorMessage?: string;
  filterSelectedOptions?: boolean;
  tooltipText?: string;
  required?: boolean;
  dataId?: string;
  onChange?: (value: SingleSelectProps['options'][number]['value']) => void;
  helperText?: string;
}

type SingleSelectFieldProps = ComponentProps<typeof ComboBox> &
  SingleSelectProps;

const SingleSelectField: React.FC<SingleSelectFieldProps> = ({
  disabled,
  label,
  options = [],
  tooltipText,
  required,
  dataId = null,
  name,
  onChange,
  helperText,
  value,
  errorMessage,
}) => {
  const handleSingleSelectChange = (
    _event: React.SyntheticEvent<Element, Event>,
    selectedOption: SingleSelectFieldProps['options'][number],
  ) => {
    onChange(selectedOption?.value);
  };

  const memoizedOptions = useMemo(() => {
    return [{ value: '', title: DEFAULT_OPTION }, ...options];
  }, [options]);

  const memoizedValue = value
    ? memoizedOptions.find((x) => String(x.value) === String(value))
    : null;

  return (
    <Box>
      <ComboBox
        disabled={disabled}
        hasError={!disabled && !!errorMessage}
        id={name}
        label={
          <InputLabel
            dataId={dataId}
            label={label}
            required={required}
            tooltipText={tooltipText}
          />
        }
        multiple={false}
        onChange={handleSingleSelectChange}
        options={memoizedOptions}
        value={memoizedValue}
        placeholder={DEFAULT_OPTION}
        description={!disabled && errorMessage}
      />
      <HelperText>{helperText}</HelperText>
    </Box>
  );
};

export default SingleSelectField;
